console.log("✅ index.tsx is running");

import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import RumProvider, { initializeRum } from "./providers/RumProvider";
import "./styles/global.css";
import { isResultsPath } from "./utils/routes";

// Initialize RUM synchronously before React renders so awsRum is never null
// when child component useEffect hooks fire (children effects run before parent effects).
// NOTE: called AFTER sessionStorage is written below, not here.

const requiredParams = ["parentLocation", "redirectUri", "formId"];
const rootElement = document.getElementById("root");

if (rootElement) {
  console.log("✅ root element found", window.location);

  // The results page is reached with assessment params instead of the form's
  // launch params, so it skips launch validation and keeps the stored ones.
  if (isResultsPath(window.location.pathname)) {
    initializeRum();
  } else if (window.location.search) {
    console.log("✅ window.location.search found", window.location.search);
    sessionStorage.setItem("searchParams", window.location.search);

    // Initialize RUM after sessionStorage is populated so session attributes
    // (formId, practiceId, tenantId, saxId) resolve to real values, not "unknown".
    initializeRum();

    // Validate required query parameters
    const urlParams = new URLSearchParams(window.location.search);

    const missingParams = requiredParams.filter(
      (param) => !urlParams.get(param),
    );

    if (missingParams.length > 0) {
      console.error("❌ Missing required query parameters:", missingParams);
      // Display error message to user
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: white;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                font-family: Arial, sans-serif;
                z-index: 9999;
            `;
      errorDiv.innerHTML = `
                <h1 style="color: #e74c3c; margin-bottom: 1rem;">Configuration Error</h1>
                <p style="color: #333; font-size: 1.2rem; margin-bottom: 0.5rem;">
                    Missing required parameters: ${missingParams.join(", ")}
                </p>
                <p style="color: #666; font-size: 1rem;">
                    Please ensure the URL includes all required query parameters.
                </p>
            `;
      document.body.appendChild(errorDiv);
      throw new Error(
        `Missing required query parameters: ${missingParams.join(", ")}`,
      );
    }

    console.log("✅ All required query parameters are present");
  } else {
    console.error("❌ No query parameters found in URL");
    // try to get search params from sessionStorage first
    const searchParams = sessionStorage.getItem("searchParams");
    if (searchParams) {
      const urlParams = new URLSearchParams(searchParams);
      const missingParams = requiredParams.filter(
        (param) => !urlParams.get(param),
      );
      if (!missingParams.length) {
        console.log("✅ Using search params from sessionStorage", searchParams);
        window.location.replace(`${window.location.pathname}?${urlParams.toString()}`); // Update the URL with search params
      }
    } else {
      // Display error message to user
      const errorDiv = document.createElement("div");
      errorDiv.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: white;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            font-family: Arial, sans-serif;
            z-index: 9999;
        `;
      errorDiv.innerHTML = `
            <h1 style="color: #e74c3c; margin-bottom: 1rem;">Configuration Error</h1>
            <p style="color: #333; font-size: 1.2rem; margin-bottom: 0.5rem;">
                No query parameters provided
            </p>
            <p style="color: #666; font-size: 1rem;">
                This application requires query parameters to function properly.
            </p>
        `;
      document.body.appendChild(errorDiv);
      throw new Error("No query parameters found in URL");
    }
  }

  const root = ReactDOM.createRoot(rootElement);

  root.render(
    <React.StrictMode>
      <RumProvider>
        <App />
      </RumProvider>
    </React.StrictMode>,
  );
} else {
  console.error("❌ Root element not found");
}
